import { useRef, useCallback } from "react";
import { observer } from "mobx-react-lite";
import classNames from "classnames";
import Icon from "@mdi/react";
import { mdiChevronRight } from "@mdi/js";
import { Button, Chip, Table, Tooltip } from "@heroui/react";
import type { FeedbackResponseDto } from "@pro-sto/api-client";
import { SkeletonRow } from "@pro-sto/ui-v2";
import { feedbackStore } from "@/stores/FeedbackStore";
import {
  FEEDBACK_STATUS_COLOR,
  FEEDBACK_STATUS_LABEL,
  formatFeedbackDate,
  truncateMessage,
} from "./constants";

interface Column {
  id: string;
  label: string;
  width?: string;
  align?: "left" | "center" | "right";
  render: (row: FeedbackResponseDto) => React.ReactNode;
}

interface FeedbackTableProps {
  onOpenTicket: (uuid: string) => void;
}

const FeedbackTable: React.FC<FeedbackTableProps> = observer(({ onOpenTicket }) => {
  const { items, isLoading, isLoadingMore, hasNextPage } = feedbackStore;

  const observerRef = useRef<IntersectionObserver | null>(null);

  const sentinelCallback = useCallback(
    (node: HTMLTableRowElement | null) => {
      if (observerRef.current) observerRef.current.disconnect();
      if (!node) return;
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting && hasNextPage && !isLoadingMore) {
            feedbackStore.loadMore();
          }
        },
        { rootMargin: "200px" },
      );
      observerRef.current.observe(node);
    },
    [hasNextPage, isLoadingMore],
  );

  const columns: Column[] = [
    {
      id: "created_at",
      label: "Дата",
      width: "140px",
      render: (row) => formatFeedbackDate(row.created_at),
    },
    {
      id: "topic",
      label: "Тема",
      width: "160px",
      render: (row) => (
        <span className="fb-table-cell--truncate" title={row.topic_name ?? undefined}>
          {row.topic_name ?? "—"}
        </span>
      ),
    },
    {
      id: "name",
      label: "Отправитель",
      width: "160px",
      render: (row) => row.name ?? "—",
    },
    {
      id: "email",
      label: "Email",
      width: "200px",
      render: (row) => (
        <span className="fb-table-cell--truncate" title={row.email ?? undefined}>
          {row.email ?? "—"}
        </span>
      ),
    },
    {
      id: "status",
      label: "Статус",
      width: "120px",
      align: "center",
      render: (row) => (
        <Chip size="sm" color={FEEDBACK_STATUS_COLOR[row.status]} variant="soft">
          {FEEDBACK_STATUS_LABEL[row.status]}
        </Chip>
      ),
    },
    {
      id: "message",
      label: "Сообщение",
      width: "280px",
      render: (row) => (
        <Tooltip delay={400}>
          <Tooltip.Trigger>
            <span className="fb-table-cell--truncate fb-table-cell--message">
              {truncateMessage(row.message)}
            </span>
          </Tooltip.Trigger>
          <Tooltip.Content className="fb-message-tooltip">
            {row.message}
          </Tooltip.Content>
        </Tooltip>
      ),
    },
    {
      id: "actions",
      label: "",
      width: "52px",
      align: "center",
      render: (row) => (
        <Button
          isIconOnly
          variant="ghost"
          size="sm"
          aria-label="Открыть"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onOpenTicket(row.uuid);
          }}
        >
          <Icon path={mdiChevronRight} size={0.9} />
        </Button>
      ),
    },
  ];

  const isEmpty = !isLoading && items.length === 0;

  return (
    <div className="fb-table-wrap">
      <Table>
        <Table.ScrollContainer>
          <Table.Content
            aria-label="Обратная связь"
            className="fb-table"
            style={{ minWidth: "1050px" }}
          >
            <Table.Header className="fb-table__head sticky top-0 z-10">
              {columns.map((col) => (
                <Table.Column
                  key={col.id}
                  id={col.id}
                  isRowHeader={col.id === "created_at"}
                  className={classNames("fb-table__th", {
                    "fb-table__th_center": col.align === "center",
                    "fb-table__th_right": col.align === "right",
                    "fb-table__th_sticky-right": col.id === "actions",
                  })}
                  style={col.width ? { width: col.width } : undefined}
                >
                  {col.label}
                </Table.Column>
              ))}
            </Table.Header>
            <Table.Body className="fb-table__body">
              {isLoading ? (
                Array.from({ length: 8 }, (_, i) => (
                  <SkeletonRow
                    key={`skel-${i}`}
                    colCount={columns.length}
                    withCheckbox={false}
                  />
                ))
              ) : items.length === 0 ? (
                <Table.Row className="fb-table__row fb-table__row--empty">
                  <Table.Cell
                    className="fb-table__empty-cell"
                    colSpan={columns.length}
                  >
                    <span aria-hidden="true">&nbsp;</span>
                  </Table.Cell>
                </Table.Row>
              ) : (
                <>
                  {items.map((row) => (
                    <Table.Row
                      key={row.uuid}
                      id={row.uuid}
                      className="fb-table__row fb-table__row--selectable"
                      onClick={() => onOpenTicket(row.uuid)}
                    >
                      {columns.map((col) => (
                        <Table.Cell
                          key={col.id}
                          className={classNames("fb-table__td", {
                            "fb-table__td_center": col.align === "center",
                            "fb-table__td_right": col.align === "right",
                            "fb-table__td_sticky-right": col.id === "actions",
                          })}
                        >
                          {col.render(row)}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  ))}
                  {hasNextPage && (
                    <Table.Row
                      ref={sentinelCallback}
                      className="fb-table__row fb-table__row--sentinel"
                    >
                      <Table.Cell
                        className="fb-table__td"
                        colSpan={columns.length}
                      >
                        <span aria-hidden="true">&nbsp;</span>
                      </Table.Cell>
                    </Table.Row>
                  )}
                </>
              )}
            </Table.Body>
          </Table.Content>
        </Table.ScrollContainer>
      </Table>
      {isEmpty && (
        <div
          className="fb-table__empty-overlay"
          role="status"
          aria-live="polite"
        >
          Нет обращений
        </div>
      )}
      {isLoadingMore && (
        <div
          className="fb-table__loading-overlay"
          role="status"
          aria-live="polite"
        >
          <span className="fb-table__loading-overlay__pill">Загрузка...</span>
        </div>
      )}
    </div>
  );
});

FeedbackTable.displayName = "FeedbackTable";
export default FeedbackTable;
